import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  ImagePlus,
  Film,
  Loader2,
  Send,
  Clock,
  Save,
  X,
  ChevronLeft,
  ChevronRight,
  Pencil,
} from 'lucide-react';
import { getSocialApiBaseUrl, uploadSocialMediaFile } from '../../../utils/socialApi';
import { readVideoFileDuration, validateVideoDuration } from './socialVideoUtils';
import {
  gymModalInput,
  gymModalLabel,
  gymSocialBtnPrimary,
  gymSocialAlertError,
  gymSocialAlertWarn,
  gymSocialBtnSecondary,
  gymSocialCard,
  gymSocialMutedText,
  gymSocialTitleText,
} from '../gymPageTheme';
import GymSocialStoryCropEditor from './GymSocialStoryCropEditor';
import GymSocialStoryMediaStrip from './GymSocialStoryMediaStrip';
import GymSocialStoryPreview from './GymSocialStoryPreview';
import {
  readImageFileDimensions,
  readImageUrlDimensions,
  storyAspectFit,
} from './socialStoryImage';

const MAX_MEDIA = 10;

function countStoryBadFit(items) {
  return items.filter((m) => !isVideoMedia(m) && m.storyFit === 'bad').length;
}

const CONTENT_TYPES = [
  { id: 'post', enabled: true },
  { id: 'story', enabled: true },
  { id: 'reel', enabled: true },
];

function isVideoMedia(item) {
  if (!item) {
    return false;
  }
  if (item.media_kind === 'video') {
    return true;
  }
  return /\.(mp4|mov)(\?|$)/i.test(item.path || '') || /\.(mp4|mov)(\?|$)/i.test(item.url || '');
}

function toDatetimeLocal(value) {
  if (!value) return '';
  return String(value).replace(' ', 'T').slice(0, 16);
}

function defaultTargetsFromLinks(socialLinks) {
  return {
    facebook: !!socialLinks?.facebook,
    instagram: !!socialLinks?.instagram,
  };
}

export default function GymSocialPostComposer({
  api,
  socialLinks = { facebook: true, instagram: true },
  onSaved,
  initialScheduledAt = '',
  editingPost = null,
  onCancelEdit,
}) {
  const { t } = useTranslation();
  const socialApi = getSocialApiBaseUrl();
  const [caption, setCaption] = useState('');
  const [targets, setTargets] = useState(() => defaultTargetsFromLinks(socialLinks));
  const [contentType, setContentType] = useState('post');
  const [scheduledAt, setScheduledAt] = useState(initialScheduledAt);
  const [media, setMedia] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [croppingStory, setCroppingStory] = useState(false);
  const [storyCropEditorIndex, setStoryCropEditorIndex] = useState(null);
  const [selectedStoryIndex, setSelectedStoryIndex] = useState(0);

  const editingId = editingPost?.id ?? null;
  const isStory = contentType === 'story';
  const isReel = contentType === 'reel';
  const isPost = contentType === 'post';
  const maxMedia = isReel ? 1 : MAX_MEDIA;
  const selectedStory = isStory ? media[selectedStoryIndex] : null;
  const storyBadCount = isStory ? countStoryBadFit(media) : 0;

  const resetForm = () => {
    setCaption('');
    setTargets(defaultTargetsFromLinks(socialLinks));
    setContentType('post');
    setScheduledAt('');
    setMedia([]);
    setSelectedStoryIndex(0);
  };

  useEffect(() => {
    if (!editingPost) {
      if (!initialScheduledAt) {
        return;
      }
      setScheduledAt(initialScheduledAt);
      return;
    }
    setCaption(editingPost.caption || '');
    setContentType(editingPost.content_type || 'post');
    setScheduledAt(toDatetimeLocal(editingPost.scheduled_at));
    setMedia(Array.isArray(editingPost.media) ? [...editingPost.media] : []);
    const tg = editingPost.targets || [];
    setTargets({
      facebook: tg.includes('facebook'),
      instagram: tg.includes('instagram'),
    });
  }, [editingPost, initialScheduledAt]);

  useEffect(() => {
    if (editingPost) {
      return;
    }
    setTargets((prev) => {
      const next = defaultTargetsFromLinks(socialLinks);
      if (prev.facebook === next.facebook && prev.instagram === next.instagram) {
        return prev;
      }
      return next;
    });
  }, [socialLinks?.facebook, socialLinks?.instagram, editingPost]);

  useEffect(() => {
    if (initialScheduledAt && !editingPost) {
      setScheduledAt(initialScheduledAt);
    }
  }, [initialScheduledAt, editingPost]);

  useEffect(() => {
    if (!isStory) {
      return;
    }
    const pending = media
      .map((item, index) => ({ item, index }))
      .filter(({ item }) => item.url && !item.storyFit);
    if (pending.length === 0) {
      return;
    }
    let cancelled = false;
    Promise.all(
      pending.map(async ({ item, index }) => {
        try {
          const dims = await readImageUrlDimensions(item.url);
          return { index, storyFit: storyAspectFit(dims.width, dims.height) };
        } catch {
          return { index, storyFit: 'warn' };
        }
      })
    ).then((updates) => {
      if (cancelled) {
        return;
      }
      setMedia((prev) => {
        const next = [...prev];
        for (const u of updates) {
          if (next[u.index]) {
            next[u.index] = { ...next[u.index], storyFit: u.storyFit };
          }
        }
        return next;
      });
    });
    return () => {
      cancelled = true;
    };
  }, [isStory, media]);

  useEffect(() => {
    if (!isStory || selectedStoryIndex < media.length) {
      return;
    }
    setSelectedStoryIndex(Math.max(0, media.length - 1));
  }, [isStory, media.length, selectedStoryIndex]);

  const toggleTarget = (key) => {
    setTargets((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleVideo = async (e) => {
    const file = (e.target.files || [])[0];
    if (!file) {
      return;
    }
    const singleVideo = isReel || isPost;
    if (singleVideo && media.length >= 1) {
      toast.error(isPost ? t('gym.social.posts.postOneVideo') : t('gym.social.posts.reelOneVideo'));
      e.target.value = '';
      return;
    }
    if (isPost && media.some((m) => !isVideoMedia(m))) {
      toast.error(t('gym.social.posts.postVideoNoMix'));
      e.target.value = '';
      return;
    }
    if (!singleVideo && media.length >= maxMedia) {
      toast.error(t('gym.social.posts.maxImages', { max: MAX_MEDIA }));
      e.target.value = '';
      return;
    }
    setUploading(true);
    try {
      const durationSec = validateVideoDuration(
        await readVideoFileDuration(file),
        contentType
      );
      const data = await uploadSocialMediaFile(file, 'video');
      const entry = {
        path: data.path,
        url: data.url,
        media_kind: data.media_kind || 'video',
        duration_sec: durationSec,
      };
      if (singleVideo) {
        setMedia([entry]);
      } else {
        setMedia((prev) => [...prev, entry]);
      }
      toast.success(t('gym.social.posts.videoUploaded'));
    } catch (err) {
      toast.error(err.response?.data?.error || err.message || t('gym.social.posts.uploadError'));
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleImages = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) {
      return;
    }
    const remaining = maxMedia - media.length;
    if (remaining <= 0) {
      toast.error(t('gym.social.posts.maxImages', { max: MAX_MEDIA }));
      e.target.value = '';
      return;
    }
    const toUpload = files.slice(0, remaining);
    if (files.length > remaining) {
      toast(t('gym.social.posts.maxImages', { max: MAX_MEDIA }), { icon: 'ℹ️' });
    }
    if (isPost && media.some(isVideoMedia)) {
      toast.error(t('gym.social.posts.postVideoNoMix'));
      e.target.value = '';
      return;
    }

    setUploading(true);
    const added = [];
    try {
      for (const file of toUpload) {
        let storyFit;
        if (isStory) {
          const dims = await readImageFileDimensions(file);
          storyFit = storyAspectFit(dims.width, dims.height);
        }
        const uploaded = await uploadSocialMediaFile(file, 'image');
        const entry = { path: uploaded.path, url: uploaded.url };
        if (isStory) {
          entry.storyFit = storyFit;
        }
        added.push(entry);
        if (isStory && storyFit === 'warn') {
          toast(t('gym.social.posts.storyFitWarnShort'), { icon: '⚠️', duration: 5000 });
        }
      }
      setMedia((prev) => [...prev, ...added]);
      const badAdded = isStory ? countStoryBadFit(added) : 0;
      if (isStory && badAdded > 0) {
        toast.error(t('gym.social.posts.storyBatchSomeBad', { count: badAdded }));
      } else {
        toast.success(
          added.length === 1
            ? t('gym.social.posts.imageUploaded')
            : t('gym.social.posts.imagesUploaded', { count: added.length })
        );
      }
    } catch (err) {
      toast.error(err.response?.data?.error || t('gym.social.posts.uploadError'));
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const removeMedia = (index) => {
    setMedia((prev) => prev.filter((_, i) => i !== index));
    if (isStory && selectedStoryIndex >= index && selectedStoryIndex > 0) {
      setSelectedStoryIndex((i) => Math.max(0, i - 1));
    }
  };

  const moveMedia = (index, direction) => {
    const target = index + direction;
    setMedia((prev) => {
      const next = [...prev];
      if (target < 0 || target >= next.length) {
        return prev;
      }
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
    if (isStory) {
      setSelectedStoryIndex((i) => {
        if (i === index) {
          return target;
        }
        if (i === target) {
          return index;
        }
        return i;
      });
    }
  };

  const buildPayload = () => {
    const selectedTargets = Object.entries(targets)
      .filter(([, v]) => v)
      .map(([k]) => k);
    return {
      content_type: contentType,
      caption,
      media,
      targets: selectedTargets,
      scheduled_at: scheduledAt || null,
    };
  };

  const openStoryCropEditor = (index = selectedStoryIndex) => {
    if (media[index]?.url) {
      setStoryCropEditorIndex(index);
    }
  };

  const handleStoryCropApply = async (blob) => {
    const index = storyCropEditorIndex;
    if (index == null || !blob) {
      return;
    }
    setCroppingStory(true);
    try {
      const file = new File([blob], `story-9x16-${index + 1}.jpg`, { type: 'image/jpeg' });
      const uploaded = await uploadSocialMediaFile(file, 'image');
      setMedia((prev) =>
        prev.map((m, i) =>
          i === index ? { path: uploaded.path, url: uploaded.url, storyFit: 'good' } : m
        )
      );
      setStoryCropEditorIndex(null);
      toast.success(t('gym.social.posts.storyCropped'));
    } catch {
      toast.error(t('gym.social.posts.storyCropError'));
    } finally {
      setCroppingStory(false);
    }
  };

  const persistPost = async (options = {}) => {
    if (media.length === 0) {
      toast.error(isReel ? t('gym.social.posts.needVideo') : t('gym.social.posts.needImage'));
      return null;
    }
    if (isReel && !isVideoMedia(media[0])) {
      toast.error(t('gym.social.posts.needVideo'));
      return null;
    }
    if (isStory && storyBadCount > 0) {
      toast.error(
        storyBadCount === 1
          ? t('gym.social.posts.storyPublishBlocked')
          : t('gym.social.posts.storyPublishBlockedMany', { count: storyBadCount })
      );
      return null;
    }
    setSaving(true);
    try {
      const payload = { ...buildPayload(), ...options };
      if (editingId) {
        const res = await api.post(
          'gym_social_posts.php?action=update',
          { id: editingId, ...payload },
          { baseURL: socialApi }
        );
        onSaved?.(res.data.post);
        return res.data.post;
      }
      const res = await api.post('gym_social_posts.php?action=create', payload, {
        baseURL: socialApi,
      });
      onSaved?.(res.data.post);
      return res.data.post;
    } catch (err) {
      toast.error(err.response?.data?.error || t('gym.social.posts.saveError'));
      return null;
    } finally {
      setSaving(false);
    }
  };

  const handleDraft = async () => {
    const post = await persistPost({ scheduled_at: null });
    if (post) {
      toast.success(editingId ? t('gym.social.posts.draftUpdated') : t('gym.social.posts.draftSaved'));
      resetForm();
      onCancelEdit?.();
    }
  };

  const minScheduleLocal = () => {
    const d = new Date(Date.now() + 3 * 60 * 1000);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const handleSchedule = async () => {
    if (!scheduledAt) {
      toast.error(t('gym.social.posts.needScheduleDate'));
      return;
    }
    if (new Date(scheduledAt).getTime() < Date.now() + 2 * 60 * 1000) {
      toast.error(t('gym.social.posts.scheduleTooSoon'));
      return;
    }
    const post = await persistPost();
    if (post) {
      toast.success(t('gym.social.posts.scheduled'));
      resetForm();
      onCancelEdit?.();
    }
  };

  const handlePublishNow = async () => {
    if (media.length === 0) {
      toast.error(isReel ? t('gym.social.posts.needVideo') : t('gym.social.posts.needImage'));
      return;
    }
    if (isReel && !isVideoMedia(media[0])) {
      toast.error(t('gym.social.posts.needVideo'));
      return;
    }
    if (isStory && storyBadCount > 0) {
      toast.error(
        storyBadCount === 1
          ? t('gym.social.posts.storyPublishBlocked')
          : t('gym.social.posts.storyPublishBlockedMany', { count: storyBadCount })
      );
      return;
    }
    setSaving(true);
    try {
      let post;
      if (editingId) {
        await api.post(
          'gym_social_posts.php?action=update',
          { id: editingId, ...buildPayload(), scheduled_at: null },
          { baseURL: socialApi }
        );
        const res = await api.post(
          'gym_social_posts.php?action=publish_now',
          { id: editingId },
          { baseURL: socialApi }
        );
        post = res.data.post;
      } else {
        const res = await api.post(
          'gym_social_posts.php?action=create',
          { ...buildPayload(), scheduled_at: null, publish_immediately: true },
          { baseURL: socialApi }
        );
        post = res.data.post;
      }
      onSaved?.(post);
      if (post.status === 'published') {
        if (post.publish_error) {
          toast(post.publish_error, { icon: '⚠️', duration: 10000 });
        } else if (isStory && media.length > 1) {
          toast.success(t('gym.social.posts.storiesPublished', { count: media.length }));
        } else if (isReel) {
          toast.success(t('gym.social.posts.reelPublished'));
        } else {
          toast.success(
            isStory ? t('gym.social.posts.storyPublished') : t('gym.social.posts.published')
          );
        }
      } else if (post.status === 'failed') {
        toast.error(post.publish_error || t('gym.social.posts.publishError'));
      }
      resetForm();
      onCancelEdit?.();
    } catch (err) {
      toast.error(err.response?.data?.error || t('gym.social.posts.publishError'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`${gymSocialCard} p-6 space-y-4 w-full max-w-full`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <h2 className={`text-lg font-semibold ${gymSocialTitleText}`}>
          {editingId ? t('gym.social.posts.editTitle') : t('gym.social.posts.composerTitle')}
        </h2>
        {editingId && (
          <button
            type="button"
            onClick={() => {
              resetForm();
              onCancelEdit?.();
            }}
            className={`text-xs underline ${gymSocialMutedText} hover:text-gray-900 dark:hover:text-white`}
          >
            {t('gym.social.posts.cancelEdit')}
          </button>
        )}
      </div>
      {editingId && (
        <p className={`text-xs flex items-center gap-1 ${gymSocialMutedText}`}>
          <Pencil className="h-3 w-3" />
          {t(`gym.social.posts.status.${editingPost.status}`)} #{editingId}
        </p>
      )}

      <div className="flex flex-wrap gap-2">
        {CONTENT_TYPES.map((ct) => (
          <button
            key={ct.id}
            type="button"
            disabled={!ct.enabled}
            onClick={() => {
              if (!ct.enabled) return;
              setContentType(ct.id);
              if (ct.id === 'reel') {
                const videos = media.filter(isVideoMedia).slice(0, 1);
                if (videos.length !== media.length) {
                  setMedia(videos);
                  if (media.length > 0 && videos.length === 0) {
                    toast(t('gym.social.posts.reelClearsImages'), { icon: 'ℹ️' });
                  }
                }
                setStoryCropEditorIndex(null);
              } else if (ct.id === 'post') {
                const videos = media.filter(isVideoMedia).slice(0, 1);
                const images = media.filter((m) => !isVideoMedia(m));
                if (videos.length > 0 && images.length > 0) {
                  setMedia(videos);
                  toast(t('gym.social.posts.postKeepsVideo'), { icon: 'ℹ️' });
                } else if (videos.length > 1) {
                  setMedia([videos[0]]);
                }
                setStoryCropEditorIndex(null);
              } else {
                setStoryCropEditorIndex(null);
              }
            }}
            className={`px-3 py-1.5 rounded-lg text-sm border transition-colors ${
              contentType === ct.id
                ? 'bg-indigo-600 text-white border-indigo-600 dark:bg-indigo-600 dark:border-indigo-500'
                : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:hover:bg-gray-700/40'
            } ${!ct.enabled ? 'opacity-40 cursor-not-allowed' : ''}`}
          >
            {t(`gym.social.posts.types.${ct.id}`)}
            {!ct.enabled && (
              <span className="ml-1 text-xs opacity-80">({t('gym.social.posts.todo')})</span>
            )}
          </button>
        ))}
      </div>

      {(socialLinks.facebook || socialLinks.instagram) && (
        <div className={`flex flex-wrap gap-4 text-sm ${gymSocialMutedText}`}>
          {socialLinks.facebook && (
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={targets.facebook}
                onChange={() => toggleTarget('facebook')}
                className="rounded border-gray-400 dark:border-white/30"
              />
              Facebook
            </label>
          )}
          {socialLinks.instagram && (
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={targets.instagram}
                onChange={() => toggleTarget('instagram')}
                className="rounded border-gray-400 dark:border-white/30"
              />
              Instagram
            </label>
          )}
        </div>
      )}

      {!isStory ? (
        <label className="block w-full">
          <span className={gymModalLabel}>{t('gym.social.posts.caption')}</span>
          <textarea
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            rows={isReel ? 3 : 4}
            className={`mt-1 ${gymModalInput}`}
            placeholder={
              isReel
                ? t('gym.social.posts.reelCaptionPlaceholder')
                : t('gym.social.posts.captionPlaceholder')
            }
          />
        </label>
      ) : (
        <p className={`text-sm ${gymSocialMutedText}`}>{t('gym.social.posts.storyNoCaption')}</p>
      )}

      <div className="space-y-2">
        <div className="flex items-center gap-3 flex-wrap">
          {(((isStory || isPost) && !media.some(isVideoMedia)) || isReel) && (
            <label
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-dashed cursor-pointer ${gymSocialBtnSecondary} ${
                isReel && media.length >= maxMedia ? 'opacity-50 pointer-events-none' : ''
              } ${isPost && media.length >= MAX_MEDIA ? 'opacity-50 pointer-events-none' : ''}`}
            >
              {uploading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <ImagePlus className="h-4 w-4" />
              )}
              {isStory ? t('gym.social.posts.addStoryImages') : t('gym.social.posts.addImage')}
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                multiple={isStory}
                className="hidden"
                onChange={handleImages}
                disabled={isStory ? media.length >= maxMedia : media.length >= MAX_MEDIA}
              />
            </label>
          )}
          {(isReel || isStory || isPost) && !media.some((m) => !isVideoMedia(m)) && (
            <label
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-dashed cursor-pointer ${gymSocialBtnSecondary} ${
                (isReel || isPost) && media.length >= 1 ? 'opacity-50 pointer-events-none' : ''
              } ${isStory && media.length >= maxMedia ? 'opacity-50 pointer-events-none' : ''}`}
            >
              {uploading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Film className="h-4 w-4" />
              )}
              {isReel
                ? t('gym.social.posts.addReelVideo')
                : isStory
                  ? t('gym.social.posts.addStoryVideo')
                  : t('gym.social.posts.addPostVideo')}
              <input
                type="file"
                accept="video/mp4,video/quicktime,.mp4,.mov"
                className="hidden"
                onChange={handleVideo}
                disabled={
                  (isReel || isPost) && media.length >= 1
                    ? true
                    : isStory && media.length >= maxMedia
                }
              />
            </label>
          )}
          <span className={`text-xs ${gymSocialMutedText}`}>
            {isReel
              ? t('gym.social.posts.reelVideoHint')
              : isStory
                ? media.length > 0
                  ? t('gym.social.posts.storyBatchCount', { count: media.length, max: MAX_MEDIA })
                  : t('gym.social.posts.storyMediaHint')
                : media.some(isVideoMedia)
                  ? t('gym.social.posts.postVideoHint')
                  : `${t('gym.social.posts.imageCount', { count: media.length, max: MAX_MEDIA })}${
                      media.length > 1 ? ` · ${t('gym.social.posts.carouselHint')}` : ''
                    }${media.length > 1 ? ` · ${t('gym.social.posts.reorderHint')}` : ''}`}
          </span>
        </div>
        {(isReel || (isPost && media.some(isVideoMedia))) && media[0]?.url && isVideoMedia(media[0]) && (
          <div className="space-y-2 max-w-[220px]">
            <video
              src={media[0].url}
              controls
              playsInline
              className="w-full aspect-[9/16] rounded-lg border border-gray-200 dark:border-white/15 bg-black object-contain"
            />
            <button
              type="button"
              onClick={() => setMedia([])}
              className={`text-xs underline ${gymSocialMutedText} hover:text-gray-900 dark:hover:text-white`}
            >
              {t('gym.social.posts.removeVideo')}
            </button>
          </div>
        )}
        {isStory && media.length > 0 && (
          <p className={`text-xs ${gymSocialMutedText}`}>{t('gym.social.posts.storyBatchHint')}</p>
        )}
        {media.length > 0 && isStory && (
          <div className="space-y-3">
            <GymSocialStoryMediaStrip
              media={media}
              selectedIndex={selectedStoryIndex}
              onSelect={setSelectedStoryIndex}
              onRemove={removeMedia}
              onMove={moveMedia}
            />
            {storyBadCount > 0 &&
              (storyBadCount > 1 ||
                selectedStory?.storyFit !== 'bad' ||
                isVideoMedia(selectedStory)) && (
              <div className={gymSocialAlertError}>
                <p>
                  {storyBadCount === 1
                    ? t('gym.social.posts.storyPublishBlocked')
                    : t('gym.social.posts.storyPublishBlockedMany', { count: storyBadCount })}
                </p>
              </div>
            )}
            {selectedStory?.url && (
              <div className="space-y-3 rounded-lg border border-gray-200 dark:border-white/15 p-4 bg-gray-50/80 dark:bg-gray-800/40">
                <p className={`text-xs font-medium ${gymSocialMutedText}`}>
                  {t('gym.social.posts.storyPreviewSlide', { n: selectedStoryIndex + 1, total: media.length })}
                </p>
                {isVideoMedia(selectedStory) ? (
                  <video
                    src={selectedStory.url}
                    controls
                    playsInline
                    className="w-full max-w-[220px] aspect-[9/16] rounded-lg border border-gray-200 dark:border-white/15 bg-black object-contain"
                  />
                ) : (
                  <GymSocialStoryPreview
                    imageUrl={selectedStory.url}
                    storyFit={selectedStory.storyFit || 'warn'}
                    cropping={croppingStory}
                    onOpenCrop={() => openStoryCropEditor(selectedStoryIndex)}
                  />
                )}
              </div>
            )}
          </div>
        )}
        {media.length > 0 && !isStory && !isReel && (
          <div className="flex flex-wrap gap-2">
            {media.map((item, index) => (
              <div key={`${item.path}-${index}`} className="flex flex-col items-center gap-1">
                <div className="relative group">
                  {isVideoMedia(item) ? (
                    <video
                      src={item.url}
                      muted
                      playsInline
                      preload="metadata"
                      className="h-20 w-20 object-cover rounded-lg border border-white/20 bg-black"
                    />
                  ) : (
                  <img
                    src={item.url}
                    alt=""
                    className="h-20 w-20 object-cover rounded-lg border border-white/20"
                  />
                  )}
                  <span className="absolute bottom-0 left-0 right-0 text-center text-[10px] font-medium bg-black/50 text-white rounded-b-lg py-0.5">
                    {index + 1}
                  </span>
                  <button
                    type="button"
                    onClick={() => removeMedia(index)}
                    className="absolute -top-1 -right-1 p-0.5 rounded-full bg-red-600 text-white shadow opacity-90 hover:opacity-100"
                    aria-label={t('gym.social.posts.removeImage')}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
                {!isStory && media.length > 1 && (
                  <div className="flex gap-0.5">
                    <button
                      type="button"
                      disabled={index === 0}
                      onClick={() => moveMedia(index, -1)}
                      className="p-1 rounded border border-gray-300 dark:border-white/20 disabled:opacity-30 hover:bg-gray-100 dark:hover:bg-white/10"
                      aria-label={t('gym.social.posts.moveImageEarlier')}
                    >
                      <ChevronLeft className="h-3 w-3" />
                    </button>
                    <button
                      type="button"
                      disabled={index === media.length - 1}
                      onClick={() => moveMedia(index, 1)}
                      className="p-1 rounded border border-gray-300 dark:border-white/20 disabled:opacity-30 hover:bg-gray-100 dark:hover:bg-white/10"
                      aria-label={t('gym.social.posts.moveImageLater')}
                    >
                      <ChevronRight className="h-3 w-3" />
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <label className="block w-full max-w-md">
        <span className={`${gymModalLabel} flex items-center gap-1`}>
          <Clock className="h-4 w-4" />
          {t('gym.social.posts.scheduleAt')}
        </span>
        <input
          type="datetime-local"
          value={scheduledAt}
          min={minScheduleLocal()}
          onChange={(e) => setScheduledAt(e.target.value)}
          className={`mt-1 ${gymModalInput}`}
        />
        <p className={`text-xs mt-1 ${gymSocialMutedText}`}>{t('gym.social.posts.scheduleHint')}</p>
      </label>

      <GymSocialStoryCropEditor
        imageUrl={storyCropEditorIndex != null ? media[storyCropEditorIndex]?.url : null}
        open={storyCropEditorIndex != null}
        onClose={() => !croppingStory && setStoryCropEditorIndex(null)}
        onApply={handleStoryCropApply}
        applying={croppingStory}
      />

      <div className="flex flex-wrap gap-2 pt-2">
        <button type="button" disabled={saving} onClick={handleDraft} className={gymSocialBtnSecondary}>
          <Save className="h-4 w-4" />
          {t('gym.social.posts.saveDraft')}
        </button>
        <button
          type="button"
          disabled={saving}
          onClick={handleSchedule}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-indigo-300 bg-indigo-50 text-indigo-800 hover:bg-indigo-100 dark:border-[#7c5ce0] dark:bg-gray-800 dark:text-[#c4b5fd] dark:hover:bg-gray-700/40 disabled:opacity-50"
        >
          <Clock className="h-4 w-4" />
          {t('gym.social.posts.schedule')}
        </button>
        <button type="button" disabled={saving} onClick={handlePublishNow} className={gymSocialBtnPrimary}>
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          {t('gym.social.posts.publishNow')}
        </button>
      </div>
    </div>
  );
}
